import { AxiosError } from 'axios';
import BillHandler from '../BillHandler';
import { PaymentsApiApiBillsBillCodeBeginPaymentGet } from '@clients/BillApi';
import { Gender, Nationality, type SubmitBuyerPassenger } from '@store/form';
import { type InvoiceEntry } from '@store/invoice';
import {
    type ReserveTransferInfoDto,
    type RoomReserve,
    RoomReserveApiRoomReserveBillCodeRegisterPassengersPost,
} from '@clients/HotelApi';
import { PrepayReserveError } from '@itinerary/Exceptions';
import { PaxType } from '@utils/types';
import { ReserveType } from '@utils/itinerary';

export default class HotelBillHandler extends BillHandler {
    override async registerPaxInfo(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        landline: string;
        destinationMobile: string;
        billId: number;
        customData: {
            rooms: RoomReserve[];
            requestFromHotel: string;
            reserveTransferInfo?: ReserveTransferInfoDto;
            isFloatingReserve?: boolean;
        };
        billCode: string;
        entries: InvoiceEntry[];
    }): Promise<{
        result: boolean;
        departureTime: Date | null;
    }> {
        await RoomReserveApiRoomReserveBillCodeRegisterPassengersPost({
            billCode: params.billCode,
            registerRoomPassengersRequest: {
                mobile: params.mobile,
                email: params.email,
                phone: params.landline,
                reserveTransferInfo:
                    params.customData.reserveTransferInfo || undefined,
                requestFromHotel: params.customData.requestFromHotel,
                passengers: params.pax
                    .filter(p => p.type === PaxType.Adult)
                    .map(pax => ({
                        isMale: pax.gender === Gender.Male,
                        firstName:
                            pax.nationality === Nationality.Iranian
                                ? pax.persianFirstName
                                : pax.firstName,
                        lastName:
                            pax.nationality === Nationality.Iranian
                                ? pax.persianLastName
                                : pax.lastName,
                        isIranian: pax.nationality === Nationality.Iranian,
                        nationalCode: pax.nationalCode,
                        nationality: pax.nationalityCode,
                        passportExpiry: pax.passportExpiry,
                        passportNumber: pax.passportNumber,
                    })),
                rooms: params.customData.rooms,
                isFloatingReserve: params.customData.isFloatingReserve,
            },
        });
        return {
            result: true,
            departureTime: null,
        };
    }

    override get detailsMaxTimeForCheck() {
        return 15 * 60 * 1000;
    }

    override get checkTimeBaseOnPax() {
        return false;
    }

    override concreteType = ReserveType.Hotel;
    /**
     * Performs the reservations that must be done just before payment
     *
     * @async
     * @type {Promise<any>}
     * @memberof BillHandler
     */
    override async prepayReservation(
        billCode: string,
        { price, useCredit }: { price?: number; useCredit?: boolean }
    ) {
        try {
            const { data } = await PaymentsApiApiBillsBillCodeBeginPaymentGet({
                billCode,
                useCredit,
            });
            if (data?.RetryAfterConfirm && data.Payable !== price) {
                throw new PrepayReserveError(
                    'قیمت تغییر یافت.',
                    false,
                    data.Payable,
                    price
                );
            }
        } catch (e) {
            if (e instanceof AxiosError) {
                if (e.response?.data?.RestartFlow) {
                    throw new PrepayReserveError(
                        'ظرفیت اتاق انتخابی شما تکمیل شده است.',
                        true
                    );
                }
            }
            throw e;
        }
    }
}
